/**
 * Feeding the boards from the platform.
 *
 * Renders nothing. It sits in the shell for the life of the session, watches
 * the platform's operational records, and installs each into the demo domain as
 * it arrives.
 *
 * Three things it is careful about:
 *
 *   - It never causes the demo chunk to load. That chunk is some three
 *     megabytes, and the work orders screen has no use for it; the merges are
 *     queued through `whenDemoLoaded`, which runs them if the domain is already
 *     here and otherwise waits without asking for it. A session that never
 *     opens a board never downloads one.
 *   - It does not drag the merge into the entry chunk either. `merge.ts` reads
 *     the demo's reference tables, so it is imported dynamically rather than at
 *     the top — by the time a merge can do anything the domain is loading
 *     anyway, and the two arrive together.
 *   - It does nothing at all under the mock adapter. The sample estate is a
 *     demonstration and stays intact, and the parity tests keep measuring the
 *     boards against the original rather than against whatever is in a database
 *     this afternoon.
 */
import { useEffect } from "react";
import { useSession } from "../../app/SessionContext";
import { useDocuments, useInspections, useWorkOrders } from "../../api/queries";
import { whenDemoLoaded } from "../domain/load";
import { clearFeed } from "./report";

/** The merge, and the tables it reads, fetched only when there is something to merge. */
const merger = () => import("./merge");

export function PlatformFeed() {
  const { source } = useSession();
  const live = source.kind === "platform";

  const orders = useWorkOrders();
  const inspections = useInspections();
  const documents = useDocuments();

  const woData = orders.data;
  const inData = inspections.data;
  const docData = documents.data;

  useEffect(() => {
    if (!live) {
      clearFeed();
      return;
    }
    if (!woData) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeWorkOrders(woData.items)));
  }, [live, woData]);

  useEffect(() => {
    if (!live || !inData) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeInspections(inData)));
  }, [live, inData]);

  useEffect(() => {
    if (!live || !docData) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeDocuments(docData)));
  }, [live, docData]);

  return null;
}
